import { Box, useMediaQuery, useTheme } from '@mui/material';
import useEmblaCarousel from 'embla-carousel-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Responsive, WidthProvider } from 'react-grid-layout';
import { INode, IPage, ITypeNode } from '../../NewFormBuilder/Context/FormBuilderContext';
import { getNodesForPage } from '../../NewFormBuilder/utils/pageUtils';
import { usePublishedForm } from '../Context/PublishedFormContext';
import { Image } from '../Nodes/Image';
import { Input } from '../Nodes/Input';
import { List } from '../Nodes/List';
import { Output } from '../Nodes/Output';
import { ShortText } from '../Nodes/ShortText';
import { Submit } from '../Nodes/Submit';
import { Title } from '../Nodes/Title';

const ResponsiveReactGridLayout = WidthProvider(Responsive) as any;

interface PeekingViewProps {
  publishedNodes: INode[];
  pages: IPage[];
}

interface PageContentProps {
  nodes: INode[];
  pageWidth: number;
}

function PageContent({ nodes, pageWidth }: PageContentProps) {
  const breakpoints = useMemo(
    () => ({
      lg: 1200,
      md: 996,
      sm: 768,
      xs: 480,
      xxs: 0,
    }),
    [],
  );

  const cols = useMemo(
    () => ({
      lg: 12,
      md: 10,
      sm: 6,
      xs: 4,
      xxs: 2,
    }),
    [],
  );

  const generateResponsiveLayouts = useMemo(() => {
    if (!nodes || nodes.length === 0) {
      return { lg: [], md: [], sm: [], xs: [], xxs: [] };
    }

    const layouts: { [key: string]: any[] } = {
      lg: [],
      md: [],
      sm: [],
      xs: [],
      xxs: [],
    };

    const sortedNodes = [...nodes].sort((a, b) => {
      const yA = a.layout.y;
      const yB = b.layout.y;
      if (yA !== yB) {
        return yA - yB;
      }
      return a.layout.x - b.layout.x;
    });

    Object.keys(cols).forEach(breakpoint => {
      const numCols = cols[breakpoint as keyof typeof cols];
      const lgCols = cols.lg;

      const isMobile = breakpoint === 'xs' || breakpoint === 'xxs';

      layouts[breakpoint] = sortedNodes.map((node, index) => {
        const lgLayout = node.layout;

        if (isMobile) {
          let cumulativeY = 0;
          for (let i = 0; i < index; i++) {
            const prevNode = sortedNodes[i];
            cumulativeY += prevNode.layout.h;
          }

          return {
            ...lgLayout,
            i: node.id,
            x: 0,
            y: cumulativeY,
            w: numCols,
          };
        }

        const ratio = numCols / lgCols;
        const newX = Math.floor(lgLayout.x * ratio);
        const newW = Math.max(1, Math.floor(lgLayout.w * ratio));

        return {
          ...lgLayout,
          i: node.id,
          x: Math.min(newX, numCols - newW),
          w: newW,
        };
      });
    });

    return layouts;
  }, [nodes, cols]);

  return (
    <Box
      sx={{
        width: `${pageWidth}px`,
        maxWidth: '100%',
        flexShrink: 0,
        minWidth: 0,
        height: '100%',
        maxHeight: '100vh',
        overflowY: 'auto',
        overflowX: 'hidden',
        '&::-webkit-scrollbar': {
          width: '8px',
        },
        '&::-webkit-scrollbar-track': {
          background: '#f1f1f1',
          borderRadius: '4px',
        },
        '&::-webkit-scrollbar-thumb': {
          background: '#888',
          borderRadius: '4px',
          '&:hover': {
            background: '#555',
          },
        },
      }}
    >
      <ResponsiveReactGridLayout
        className="layout"
        layouts={generateResponsiveLayouts}
        breakpoints={breakpoints}
        cols={cols}
        rowHeight={30}
        isDraggable={false}
        isResizable={false}
        verticalCompact={false}
        margin={[10, 10]}
        containerPadding={[10, 10]}
      >
        {nodes.map(node => (
          <div
            key={node.id}
            style={{
              border: `${node.data.styles.borderWeight}px solid ${node.data.styles.borderColor}`,
              borderRadius: node.data.styles.borderRadius,
              backgroundColor: node.data.styles.backgroundColor,
              boxSizing: 'border-box',
              position: 'relative',
            }}
          >
            <div
              style={{
                width: '100%',
                height: '100%',
                padding: `${
                  node.data.styles.padding !== undefined &&
                  node.data.styles.padding !== null
                    ? node.data.styles.padding
                    : 16
                }px`,
                boxSizing: 'border-box',
                display: 'flex',
                alignItems: node.data.styles.verticalAlign,
              }}
            >
              {node.type === ITypeNode.TITLE && <Title node={node} />}

              {node.type === ITypeNode.SHORT_TEXT && (
                <ShortText node={node} />
              )}

              {node.type === ITypeNode.IMAGE && <Image node={node} />}

              {node.type === ITypeNode.LIST && <List node={node} />}

              {node.type === ITypeNode.INPUT && <Input node={node} />}

              {node.type === ITypeNode.OUTPUT && <Output node={node} />}

              {node.type === ITypeNode.SUBMIT && <Submit node={node} />}
            </div>
          </div>
        ))}
      </ResponsiveReactGridLayout>
    </Box>
  );
}

export function PeekingView({
  publishedNodes,
  pages,
}: PeekingViewProps) {
  const { redirectPageIdAfterSubmit, clearRedirectPageId, isSubmitting } = usePublishedForm();
  const previousSubmittingRef = useRef(isSubmitting);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.between('sm', 'md'));
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'));

  const [viewportWidth, setViewportWidth] = useState(
    typeof window !== 'undefined' ? window.innerWidth : 1300
  );

  useEffect(() => {
    const handleResize = () => {
      setViewportWidth(window.innerWidth);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const pageNodes = useMemo(() => {
    if (!pages || pages.length === 0) {
      return [];
    }

    return pages.map(page => ({
      page,
      nodes: getNodesForPage(page, publishedNodes || []),
    }));
  }, [pages, publishedNodes]);


  const { containerWidth, peekWidth, gap, visibleWidth } = useMemo(() => {
    if (isMobile) {
      
      const padding = viewportWidth <= 360 ? 16 : 32; 
      const availableWidth = viewportWidth - padding;
      const minPeekWidth = viewportWidth <= 360 ? 25 : 40; 
      const calculatedGap = viewportWidth <= 360 ? 8 : 12;
      
      if (viewportWidth <= 360) {
        const fixedPeekWidth = 25;
        const fixedGap = 8;
        const fixedContainerWidth = availableWidth - (fixedPeekWidth * 2) - (fixedGap * 2);
        
        return {
          containerWidth: Math.max(220, fixedContainerWidth),
          peekWidth: fixedPeekWidth,
          gap: fixedGap,
          visibleWidth: viewportWidth,
        };
      }
      
      const percentagePeek = Math.floor(availableWidth * 0.12);
      const calculatedPeekWidth = Math.max(minPeekWidth, percentagePeek);
      
      const totalPeekAndGaps = (calculatedPeekWidth * 2) + (calculatedGap * 2);
      const calculatedContainerWidth = availableWidth - totalPeekAndGaps;
      
      const minContainerWidth = 280;
      const finalContainerWidth = Math.max(minContainerWidth, calculatedContainerWidth);
      
      const remainingForPeek = availableWidth - finalContainerWidth - (calculatedGap * 2);
      const finalPeekWidth = Math.max(minPeekWidth, Math.floor(remainingForPeek / 2));
      const finalVisibleWidth = finalPeekWidth + calculatedGap + finalContainerWidth + calculatedGap + finalPeekWidth;
      
      return {
        containerWidth: finalContainerWidth,
        peekWidth: finalPeekWidth,
        gap: calculatedGap,
        visibleWidth: finalVisibleWidth,
      };
    } else if (isTablet) {
      
      const padding = 48; 
      const availableWidth = viewportWidth - padding;
      const calculatedGap = 20;
      
      if (viewportWidth <= 1024) {
        const minPeekWidth = 50;
        const minContainerWidth = 700;
        
        const calculatedPeekWidth = Math.max(minPeekWidth, Math.floor(availableWidth * 0.12));
        const calculatedContainerWidth = availableWidth - (calculatedPeekWidth * 2) - (calculatedGap * 2);
        
        if (calculatedContainerWidth < minContainerWidth) {
          const finalContainerWidth = minContainerWidth;
          const remainingForPeek = availableWidth - finalContainerWidth - (calculatedGap * 2);
          const finalPeekWidth = Math.max(minPeekWidth, Math.floor(remainingForPeek / 2));
          const finalVisibleWidth = finalPeekWidth + calculatedGap + finalContainerWidth + calculatedGap + finalPeekWidth;
          
          return {
            containerWidth: finalContainerWidth,
            peekWidth: finalPeekWidth,
            gap: calculatedGap,
            visibleWidth: finalVisibleWidth,
          };
        }
        
        const finalVisibleWidth = calculatedPeekWidth + calculatedGap + calculatedContainerWidth + calculatedGap + calculatedPeekWidth;
        
        return {
          containerWidth: calculatedContainerWidth,
          peekWidth: calculatedPeekWidth,
          gap: calculatedGap,
          visibleWidth: finalVisibleWidth,
        };
      }
      
      const minPeekWidth = 60;
      const calculatedPeekWidth = Math.max(minPeekWidth, Math.floor(availableWidth * 0.15));
      const calculatedContainerWidth = availableWidth - (calculatedPeekWidth * 2) - (calculatedGap * 2);
      const finalContainerWidth = Math.max(600, calculatedContainerWidth);
      
      const remainingForPeek = availableWidth - finalContainerWidth - (calculatedGap * 2);
      const finalPeekWidth = Math.max(minPeekWidth, Math.floor(remainingForPeek / 2));
      const finalVisibleWidth = finalPeekWidth + calculatedGap + finalContainerWidth + calculatedGap + finalPeekWidth;
      
      return {
        containerWidth: finalContainerWidth,
        peekWidth: finalPeekWidth,
        gap: calculatedGap,
        visibleWidth: finalVisibleWidth,
      };
    } else {
      
      const padding = 64; 
      const maxWidth = Math.min(viewportWidth - padding, 1800);
      const calculatedGap = 32;
      const calculatedPeekWidth = Math.floor(maxWidth * 0.3); 
      const calculatedContainerWidth = maxWidth - (calculatedPeekWidth * 2) - (calculatedGap * 2);
      
      const finalContainerWidth = Math.max(1000, Math.min(1300, calculatedContainerWidth)); 
      const finalPeekWidth = Math.min(calculatedPeekWidth, Math.floor((maxWidth - finalContainerWidth - (calculatedGap * 2)) / 2));
      const finalVisibleWidth = finalPeekWidth + calculatedGap + finalContainerWidth + calculatedGap + finalPeekWidth;
      
      return {
        containerWidth: finalContainerWidth,
        peekWidth: finalPeekWidth,
        gap: calculatedGap,
        visibleWidth: finalVisibleWidth,
      };
    }
  }, [isMobile, isTablet, isDesktop, viewportWidth]);

  const slideWidth = containerWidth + gap;

  const [emblaRef, emblaApi] = useEmblaCarousel(
    {
      loop: false,
      align: 'center',
      slidesToScroll: 1,
      containScroll: false,
      watchSlides: true,
    },
    []
  );

  const scrollNext = useCallback(() => {
    if (emblaApi) emblaApi.scrollNext();
  }, [emblaApi]);

  const scrollPrev = useCallback(() => {
    if (emblaApi) emblaApi.scrollPrev();
  }, [emblaApi]);

  const handleSlideClick = useCallback((clickedIndex: number, e: React.MouseEvent) => {    
    if (emblaApi) {
      const currentIndex = emblaApi.selectedScrollSnap();
      
      if (clickedIndex < currentIndex) {
        scrollPrev();
      }
   
      else if (clickedIndex > currentIndex) {
        scrollNext();
      }
    }
  }, [emblaApi, scrollNext, scrollPrev]);

  useEffect(() => {
    const submitJustFinished = previousSubmittingRef.current && !isSubmitting;
    previousSubmittingRef.current = isSubmitting;

    if (submitJustFinished && redirectPageIdAfterSubmit && emblaApi && pageNodes.length > 0) {
      const targetPageIndex = pageNodes.findIndex(
        ({ page }) => page.id === redirectPageIdAfterSubmit
      );

      if (targetPageIndex !== -1) {
        emblaApi.scrollTo(targetPageIndex, false);
        clearRedirectPageId();
      } else {
        clearRedirectPageId();
      }
    }
  }, [isSubmitting, redirectPageIdAfterSubmit, emblaApi, pageNodes, clearRedirectPageId]);

  if (pageNodes.length === 0) {
    return null;
  }

  return (
    <>
      <Box
        className="embla"
        sx={{
          width: '100%',
          maxWidth: {
            xs: '100%',
            sm: `${visibleWidth}px`,
          },
          overflowX: 'hidden',
          overflowY: 'hidden',
          position: 'relative',
          margin: '0 auto',
          height: '100vh',
          padding: {
            xs: '0 16px',
            sm: '0 24px',
            md: '0 32px',
          },
          '& .embla__viewport': {
            overflowX: 'visible',
            overflowY: 'hidden',
            width: '100%',
            height: '100%',
            marginLeft: {
              xs: '-16px',
              sm: '-24px',
              md: '-32px',
            },
            paddingLeft: {
              xs: '16px',
              sm: '24px',
              md: '32px',
            },
          },
          '& .embla__container': {
            display: 'flex',
            touchAction: 'pan-x pan-y pinch-zoom',
            alignItems: 'stretch',
            height: '100%',
          },
          '& .embla__slide': {
            flex: `0 0 ${slideWidth}px`,
            minWidth: 0,
            position: 'relative',
            marginRight: `${gap}px`,
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
          },
        }}
      >
        <div className="embla__viewport" ref={emblaRef}>
          <Box className="embla__container">
            {pageNodes.map(({ page, nodes }, index) => (
              <Box
                key={page.id}
                className="embla__slide"
                onClick={(e) => handleSlideClick(index, e)}
                sx={{
                  cursor: 'pointer',
                }}
              >
                <PageContent
                  nodes={nodes}
                  pageWidth={containerWidth}
                />
              </Box>
            ))}
          </Box>
        </div>
      </Box>
    </>
  );
}

